import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CreditContext = createContext(null);

const authCfg = () => {
  const t = localStorage.getItem("uc_token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export function CreditProvider({ children }) {
  const { user } = useAuth();
  const [credit, setCredit] = useState(0);
  const [promo, setPromo] = useState(0);
  const [wagerRequired, setWagerRequired] = useState(0);

  const refreshCredits = useCallback(async () => {
    if (!localStorage.getItem("uc_token")) { setCredit(0); setPromo(0); setWagerRequired(0); return; }
    try {
      const { data } = await axios.get(`${API}/credits`, authCfg());
      setCredit(Number(data.credits || 0));
      setPromo(Number(data.promo_credits || 0));
      setWagerRequired(Number(data.wager_required || 0));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user) refreshCredits(); else setCredit(0);
  }, [user, refreshCredits]);

  // Start a Stripe Checkout deposit; resolves to the hosted checkout URL
  const startDeposit = async (usd, promoCode) => {
    const { data } = await axios.post(
      `${API}/payments/checkout`,
      { amount: usd, origin_url: window.location.origin, promo_code: promoCode || null },
      authCfg()
    );
    return data.checkout_url;
  };

  const validatePromo = async (code, amount) => {
    const { data } = await axios.post(
      `${API}/promo/validate`, { code, amount: amount || 0 }, authCfg()
    );
    return data; // { valid, bonus, pct, max_bonus, message }
  };

  // Server-authoritative solo case open: debits balance + draws item server-side
  const openCase = async (caseId) => {
    const { data } = await axios.post(`${API}/cases/${caseId}/open`, {}, authCfg());
    refreshCredits();
    return data; // { item, inventory_id, credits }
  };

  const sellItem = async (inventoryId) => {
    const { data } = await axios.post(`${API}/inventory/${inventoryId}/sell`, {}, authCfg());
    refreshCredits();
    return data.payout;
  };

  const requestShip = async (itemIds, ship) => {
    const { data } = await axios.post(`${API}/withdrawals/ship`, { item_ids: itemIds, ...ship }, authCfg());
    return data;
  };

  const cashoutItems = async (itemIds, payload) => {
    const { data } = await axios.post(`${API}/withdrawals/cashout`, { item_ids: itemIds, ...payload }, authCfg());
    return data;
  };

  const demoOpen = async (cid) => {
    const { data } = await axios.get(`${API}/cases/${cid}/demo`);
    return data.item;
  };

  const listWithdrawals = async () => {
    const { data } = await axios.get(`${API}/withdrawals`, authCfg());
    return data.withdrawals || [];
  };

  return (
    <CreditContext.Provider value={{ credit, promo, wagerRequired, startDeposit, validatePromo, openCase, sellItem, requestShip, cashoutItems, listWithdrawals, demoOpen, refreshCredits }}>
      {children}
    </CreditContext.Provider>
  );
}

export const useCredit = () => useContext(CreditContext);
